import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box, LinearProgress, linearProgressClasses } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import GoogleReviewCard from '../assets/images/GoogleReviewCard.png';
import CloseIcon from '../assets/icons/actions/CloseIcon';
import GoogleIcon from '../assets/logo/GoogleIcon';
import ScreenShotIcon from '../assets/logo/ScreenShotIcon';
import CustomButton from '../components-library/buttons/CustomButton';
import RedeemableItemsCarousel from '../components-library/cashback/RedeemableItemsCarousel';
import ButtonFooter from '../components-library/footers/ButtonFooter';
import LoadingWithText from '../components-library/loading/LoadingWithText';
import TextInput from '../components-library/inputs/TextInput';
import useGooglePace from '../hooks/google-review/useGooglePlace';
import useGoogleReview from '../hooks/google-review/useGoogleReview';
import ConfirmCancelModal from '../modals/ConfirmCancelModal';
import PromotionManager from '../services/api/PromotionManager';
import { SCREENS } from '../services/exports/Constants';
import { ButtonTypes, CompanyDetailsInterface } from '../services/exports/Interfaces';
import useTheme from '../hooks/ui/useTheme';
import Theme from '../components-library/Theme';
import useAlert from '../hooks/utility/useAlert';
import classnames from 'classnames';
import Divider from '../components-library/Divider';
import LoadingPage from '../components-library/loading/LoadingPage';
import { StoreInterface } from '../store/types';
import useAuth from '../hooks/global/useAuth';
import useAppMethods from '../hooks/utility/useAppMethods';
import useCashbackProgram from '../hooks/cashback/useCashbackProgram';
import ProfileManager from '../services/api/ProfileManager';
import useRouter from '../hooks/navigation/useRouter';
import useMobileApp from '../hooks/mobile-app/useMobileApp';

interface LocationStateInterface {
  prevPage: SCREENS;
  rating?: number;
  company?: CompanyDetailsInterface;
}

enum PAGES {
  GOOGLE_REVIEW = 0,
  UPLOAD_SCREEN_SHOT = 1,
}

interface Props {
  company?: CompanyDetailsInterface;
}

const UploadGoogleReviewScreen: FC<Props> = (props: Props): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:UploadGoogleReviewScreen' });
  const location = useLocation();
  const { company: defaultCompany } = useSelector((state: StoreInterface) => state.initialData);
  const locationState: LocationStateInterface = location?.state;
  const company = props?.company ?? locationState?.company ?? defaultCompany;

  const theme = useTheme();
  const { isLoggedIn } = useAuth();
  const { toggleLoginModal } = useAppMethods();
  const { isMobileApp } = useMobileApp();
  const { navigate, goBack: mobileGoBack } = useRouter();
  const { getPlace } = useGooglePace();
  const alert = useAlert();
  const { hasCashbackProgram, cashbackProgram, rewardProducts, pointsToMoney } = useCashbackProgram({ company });
  const loading = false;
  const suggestedProducts = rewardProducts;
  const maxDiscount = pointsToMoney(cashbackProgram?.max_spending_limit ?? 0);
  const {
    hasUploadedGoogleReview,
    loading: loadingGoogleReview,
    throwGoogleReviewUploadedToast,
  } = useGoogleReview({ company });

  const rating = locationState?.rating ?? 5;
  const prevPage = locationState?.prevPage;

  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [page, setPage] = useState<PAGES>(PAGES.GOOGLE_REVIEW);
  const [screenShotFile, setScreenShotFile] = useState<null | Blob>(null);
  const [name, setName] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [processingReview, setProcessingReview] = useState<boolean>(false);
  const [showNameMissingAlert, setShowNameMissingAlert] = useState<boolean>(false);

  const inputFile = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    !isLoggedIn &&
      toggleLoginModal({
        openModal: true,
      });

    setTimeout(() => setInitialLoading(false), 1000);
  }, []);

  useEffect(() => {
    if (!loading && !initialLoading && !loadingGoogleReview) {
      setTimeout(
        () =>
          stepsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start',
          }),
        1000,
      );
    }
  }, [loading, initialLoading, loadingGoogleReview]);

  function openGoogleReviewPage(): void {
    company?.google_places_id && window.open(getPlace(company?.google_places_id), '_blank');
  }

  function openFiles(): void {
    if (hasUploadedGoogleReview) {
      return alert.error({
        description: t('error_messages.review_already_uploaded'),
      });
    }

    inputFile.current.click();
  }

  function validateBeforeUpload() {
    if (!name) {
      return toggleNameAlert();
    }

    uploadScreenShot();
  }

  function toggleNameAlert(): void {
    setShowNameMissingAlert(!showNameMissingAlert);
  }

  function goBack(state?: object): void {
    if (typeof prevPage === 'string') {
      return navigate({
        web: {
          to: prevPage,
          options: {
            absolute: true,
            state: {
              ...state,
              prevPage: location.pathname,
            },
          },
        },
      });
    }

    if (isMobileApp) {
      return mobileGoBack();
    }

    return navigate({
      web: {
        to: SCREENS.HOME_SCREEN,
        options: {
          state: {
            ...state,
            prevPage: location.pathname,
          },
        },
      },
    });
  }

  function scrollToTop(): void {
    bodyRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });
  }

  async function uploadScreenShot(): Promise<void> {
    const data = new FormData();
    data.append('name', name);
    data.append('screenshot', screenShotFile);

    setUploading(true);
    const { response, success } = await PromotionManager.uploadGoogleReview(company?.id, data, onUploadProgress);
    setUploading(false);

    if (success) {
      return uploadSuccessHandler();
    }

    uploadErrorHandler(response);
  }

  function onUploadProgress(progress: number) {
    setUploadProgress(progress);
  }

  async function uploadSuccessHandler(): Promise<void> {
    setProcessingReview(true);
    hasCashbackProgram && ProfileManager.getCashbackAccount(company?.id);

    setTimeout(() => {
      setProcessingReview(false);

      goBack({
        review: true,
        rating,
      });

      throwGoogleReviewUploadedToast();
    }, 3000);
  }

  function uploadErrorHandler(error: any): void {
    switch (error?.response?.status) {
      case 403:
        return alert.error({
          description: t('error_messages.review_already_uploaded'),
        });
      default:
        return alert.error({
          description: t('error_messages.upload_failed'),
        });
    }
  }

  function onChangeFile(event: any): void {
    event.stopPropagation();
    event.preventDefault();
    setScreenShotFile(event.target.files[0]);
    setInitialLoading(true);
    setPage(PAGES.UPLOAD_SCREEN_SHOT);
    setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
  }

  const getScreenShotUrl = useCallback<() => string>(() => {
    return URL.createObjectURL(screenShotFile);
  }, [screenShotFile]);

  function renderSeparator(): ReactElement {
    return <div className={separatorContainer} />;
  }

  const onBackClick = () => {
    if (page === PAGES.GOOGLE_REVIEW) {
      return goBack();
    }

    setPage(PAGES.GOOGLE_REVIEW);
  };

  const renderBackButton = () => (
    <button type="button" onClick={onBackClick} className={returnLinkStyle}>
      <ChevronLeft className="w-4 h-4" />
      {t('buttons.return')}
    </button>
  );

  const renderLoadingPage = useMemo<ReactElement>(() => {
    return initialLoading || loading || loadingGoogleReview ? <LoadingPage /> : null;
  }, [initialLoading, loading, loadingGoogleReview]);

  const renderProcessingReview = useMemo<ReactElement>(() => {
    return processingReview ? <LoadingWithText description={t('loading.processing')} /> : null;
  }, [processingReview]);

  const renderNameMissingAlert = useMemo(() => {
    function onConfirm(): void {
      scrollToTop();
      toggleNameAlert();
    }

    return (
      <ConfirmCancelModal
        openModal={showNameMissingAlert}
        description={t('error_messages.name_missing.description')}
        primaryButtonTitle={t('error_messages.name_missing.button.title')}
        customGradient={'bg-gradient-to-r from-brand-primary via-brand-primary to-brand-primary'}
        onConfirm={onConfirm}
      />
    );
  }, [showNameMissingAlert]);

  function renderProductSuggestions(): ReactElement {
    if (!hasCashbackProgram || !suggestedProducts?.length) {
      return null;
    }

    return (
      <div className="mt-md">
        <RedeemableItemsCarousel items={suggestedProducts} maxDiscount={maxDiscount} />
      </div>
    );
  }

  interface StepInterface {
    icon: ReactElement;
    title: string;
    description: string;
    button: ReactElement;
    children?: ReactElement;
  }

  function renderSteps(): ReactElement {
    const steps: StepInterface[] = [
      {
        icon: <GoogleIcon />,
        title: t('steps.step_1.title'),
        description: t('steps.step_1.description', {
          name: company?.name,
        }),
        button: renderGoogleReviewButton(),
      },
      {
        icon: <ScreenShotIcon color={theme.color.brand.text.primary} className="w-4 h-4" />,
        title: t('steps.step_2.title'),
        description: t('steps.step_2.description'),
        button: renderUploadScreenShotButton(),
        children: <img src={GoogleReviewCard} alt={'GoogleReviewCard'} className={googleReviewCardStyle} />,
      },
    ];

    return (
      <div className="mt-lg">
        <h6>{t('steps.how_it_works')}</h6>
        {steps?.map((step: StepInterface, index: number) => (
          <div
            className={classnames('py-lg', {
              'border-t': steps?.length - 1 === index,
            })}
            key={`google-review-steps-${index}`}
          >
            <div className={stepItemContainer} key={index?.toString()}>
              <div className={stepIconContainer}>{step?.icon}</div>
              <div>
                <h6 className="mb-2xs text-sm">
                  {step?.title} {index + 1}
                </h6>
                <p className="text-sm text-muted-foreground">{step?.description}</p>
              </div>
            </div>
            {step?.children && <div className="mt-md">{step?.children}</div>}
            <div className="mt-md">{step?.button}</div>
          </div>
        ))}
      </div>
    );
  }

  function renderReviewOnGooglePage(): ReactElement {
    return (
      <div className={wrapper}>
        {renderProductSuggestions()}
        <div className="mt-lg">
          <Divider size="large" />
        </div>
        <div ref={stepsRef}>{renderSteps()}</div>
      </div>
    );
  }

  function renderEnterPostedName(): ReactElement {
    return (
      <div className={enterPostedNameContainer}>
        <h4>{t('enter_name.title')}</h4>
        {renderSeparator()}
        <TextInput
          value={name}
          onChange={setName}
          placeholder={t('enter_name.place_holder')}
          inputContainerStyle="bg-gray-500 dark:bg-black"
        />
      </div>
    );
  }

  function renderScreenShot(): ReactElement {
    return (
      <div>
        {renderInputFile()}
        <h4>{t('screenshot_uploaded.title')}</h4>
        {renderSeparator()}
        <p className="text-sm text-muted-foreground">{t('screenshot_uploaded.description')}</p>
        {renderSeparator()}
        <div className={screenShotContainer}>
          <img src={getScreenShotUrl()} alt="screenShot" className={screenShotStyle} />
          <button className={closeIconContainer} onClick={openFiles}>
            <CloseIcon color={Theme.color.white['100']} />
          </button>
        </div>
      </div>
    );
  }

  function uploadReviewPage(): ReactElement {
    return (
      <>
        <div className={wrapper}>
          {renderEnterPostedName()}
          {renderScreenShot()}
        </div>
        {renderSubmitScreenShotButton()}
      </>
    );
  }

  function renderInputFile(): ReactElement {
    return (
      <input
        type="file"
        accept="image/png, image/jpeg"
        id="file"
        ref={inputFile}
        style={{ display: 'none' }}
        onChange={onChangeFile}
      />
    );
  }

  const renderGoogleReviewButton = (): ReactElement => {
    const renderGoogleIcon = (): ReactElement => {
      return (
        <div className={iconContainer}>
          <GoogleIcon className={googleIcon} />
        </div>
      );
    };

    return (
      <CustomButton
        title={t('buttons.leave_google_review')}
        loading={loading}
        buttonType={ButtonTypes.secondary}
        customIcon={renderGoogleIcon()}
        onClick={openGoogleReviewPage}
        containerStyle={customButtonContainerStyle}
      />
    );
  };

  const renderUploadScreenShotButton = (): ReactElement => {
    const renderScreenShotIcon = (): ReactElement => {
      return (
        <div className={iconContainer}>
          <ScreenShotIcon className={screenShotIcon} color={theme.buttons[ButtonTypes.primary].text} />
        </div>
      );
    };

    return (
      <>
        {renderInputFile()}
        <CustomButton
          title={t('buttons.upload_screenshot')}
          loading={loading}
          customIcon={renderScreenShotIcon()}
          onClick={openFiles}
          containerStyle={customButtonContainerStyle}
        />
      </>
    );
  };

  function renderSubmitScreenShotButton(): ReactElement {
    const BorderLinearProgress = styled(LinearProgress)(() => ({
      height: 8,
      borderRadius: 5,
      [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: Theme.color.gray['500'],
      },
      [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: Theme.color.brand.primary,
      },
    }));

    return (
      <ButtonFooter className={buttonContainer}>
        <div className="lg:w-1/2 w-full mx-auto">
          {uploading && (
            <div className={progressBarContainer}>
              <Box sx={{ width: '100%' }}>
                <BorderLinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            </div>
          )}
          <CustomButton title={t('buttons.submit_screenshot')} loading={uploading} onClick={validateBeforeUpload} />
        </div>
      </ButtonFooter>
    );
  }

  function renderBody(): ReactElement {
    switch (page) {
      case PAGES.GOOGLE_REVIEW:
        return renderReviewOnGooglePage();
      case PAGES.UPLOAD_SCREEN_SHOT:
        return uploadReviewPage();
    }
  }

  return (
    <div className="min-h-screen bg-gray-background">
      <div className={container} ref={bodyRef}>
        {!isMobileApp && <div className="layout-container">{renderBackButton()}</div>}
        {renderBody()}
        {renderProcessingReview}
        {renderLoadingPage}
        {renderNameMissingAlert}
      </div>
    </div>
  );
};

export default UploadGoogleReviewScreen;

const container = 'h-full w-full relative';

const returnLinkStyle =
  'flex items-center gap-2xs text-sm text-brand-text-primary hover:text-brand-primary transition-colors pt-md';

const wrapper = 'lg:w-1/2 w-full mx-auto lg:px-none px-md pt-md h-fit';

const buttonContainer = 'w-full flex flex-col justify-center items-center lg:px-none px-md lg:sticky fixed bottom-0';

const separatorContainer = 'mb-md';

const progressBarContainer = 'mb-xs w-full';

const iconContainer = 'mr-xs';

const stepItemContainer = 'flex flex-row';

const stepIconContainer =
  'rounded-full bg-white dark:bg-black-600 shadow-icon dark:shadow-none h-[33px] aspect-square flex justify-center items-center mr-md';

const googleIcon = 'w-5 h-5';

const screenShotIcon = 'w-5 h-6';

const googleReviewCardStyle = 'lg:w-8/12 w-full';

const enterPostedNameContainer = 'mb-xl';

const screenShotContainer = 'w-full bg-gray-500 dark:bg-black rounded-2xl relative ';

const screenShotStyle = 'w-full h-full object-cover';

const closeIconContainer =
  'w-[36px] h-[36px] rounded-full bg-black flex justify-center items-center absolute -top-1 -right-2 shadow-icon dark:shadow-none';

const customButtonContainerStyle = 'py-sm';
